// A tiny IndexedDB store for records that must survive a closed tab (the field outbox), keyed by `key`, listed by
// `userId`. Every call is safe: no IndexedDB (a private window, blocked storage) means nothing is kept, never an error.

const DB = 'rfid-dashboard';
const STORE = 'outbox';
const VERSION = 1;

let opening: Promise<IDBDatabase | null> | null = null;
function db(): Promise<IDBDatabase | null> {
  opening ??= new Promise((resolve) => {
    try {
      if (!('indexedDB' in window)) return resolve(null);
      const req = indexedDB.open(DB, VERSION);
      req.onupgradeneeded = () => {
        const s = req.result.createObjectStore(STORE, { keyPath: 'key' });
        s.createIndex('userId', 'userId');
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return opening;
}

async function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  const d = await db();
  if (!d) return null;
  return new Promise((resolve) => {
    try {
      const r = fn(d.transaction(STORE, mode).objectStore(STORE));
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export interface StoredRecord { key: string; userId: number; savedAt: number }
export const idbPut = <R extends StoredRecord>(rec: R) => run('readwrite', (s) => s.put(rec));
export const idbDelete = (key: string) => run('readwrite', (s) => s.delete(key));
export const idbAllFor = async <R extends StoredRecord>(userId: number) => ((await run('readonly', (s) => s.index('userId').getAll(userId))) ?? []) as R[];
/** Records older than `maxAgeMs` (any user) are removed. */
export async function idbPurgeOlder(maxAgeMs: number) {
  const all = ((await run('readonly', (s) => s.getAll())) ?? []) as StoredRecord[];
  const limit = Date.now() - maxAgeMs;
  await Promise.all(all.filter((r) => r.savedAt < limit).map((r) => idbDelete(r.key)));
}
